// AlertsView.jsx
// Live V Pattern alert monitor
// Left: compact alert panel — one row per symbol+ema+timeframe (latest only)
//       click to expand full history for that combination
// Right: chart panel
// Top: collapsible settings panel

import React, { useState, useEffect, useRef } from 'react';
import Chart from './Chart';
import useStore from '../store';
import { startSSE, stopSSE } from '../store';
import {
  fetchAlertsToday, deleteAlertHistory,
  fetchAlertSettings, saveAlertSettings,
  fetchAllWatchlists, fetchWatchlistStocks, fetchStocks,
} from '../api';
import API_BASE from '../config';

const TIMEFRAMES  = ['2min', '5min', '15min', '1H'];
const EMA_OPTIONS = ['ema_5', 'ema_9', 'ema_20', 'ema_50', 'ema_200'];
const EMA_LABEL   = (e) => e.replace('ema_', 'EMA ');

// ── Pill multi-select ──────────────────────────────────────
function MultiSelect({ options, selected, onChange, labelFn, allLabel }) {
  const allSelected = selected.length === 0;
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };
  return (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      <button onClick={() => onChange([])}
        style={{ ...pill, ...(allSelected ? pillActive : {}) }}>
        {allLabel || 'All'}
      </button>
      {options.map(opt => (
        <button key={opt} onClick={() => toggle(opt)}
          style={{ ...pill, ...(selected.includes(opt) ? pillActive : {}) }}>
          {labelFn ? labelFn(opt) : opt}
        </button>
      ))}
    </div>
  );
}

// ── Single-select pill (All / Yes / No) ───────────────────
function SingleSelect({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)}
          style={{ ...pill, ...(value === opt.value ? pillActive : {}) }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const pill = {
  padding: '3px 8px', border: '1px solid #ddd', borderRadius: '12px',
  background: '#f5f5f5', cursor: 'pointer', fontSize: '11px', color: '#555',
};
const pillActive = { background: '#2962ff', color: '#fff', borderColor: '#2962ff' };

// ── Compact summary row ────────────────────────────────────
function SummaryRow({ group, isExpanded, onToggle, onAlertClick }) {
  const latest  = group[group.length - 1];
  const isBuy   = latest.direction === 'buy';
  const color   = isBuy ? '#2e7d32' : '#c62828';
  const bgColor = isBuy ? '#e8f5e9' : '#ffebee';
  const arrow   = isBuy ? '↑' : '↓';

  const fmt = (pt) => {
    const d   = new Date(pt);
    const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
    return `${String(ist.getUTCHours()).padStart(2,'0')}:${String(ist.getUTCMinutes()).padStart(2,'0')}`;
  };

  return (
    <div style={{ marginBottom: '2px' }}>
      {/* Summary row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '6px 10px', background: bgColor, borderRadius: '4px',
        cursor: 'pointer', borderLeft: `3px solid ${color}`,
      }}
        onClick={() => onAlertClick(latest)}
      >
        <span style={{ color, fontWeight: 'bold', fontSize: '13px' }}>{arrow}</span>
        <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#131722', minWidth: '55px' }}>
          {latest.symbol}
        </span>
        <span style={{ fontSize: '11px', color: '#666', minWidth: '38px' }}>
          {EMA_LABEL(latest.ema)}
        </span>
        <span style={{ fontSize: '11px', color: '#888', marginLeft: 'auto' }}>
          {fmt(latest.pattern_time)}
        </span>
        {group.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '10px', color: '#888', padding: '0 2px',
            }}
          >
            {isExpanded ? '▲' : `▼ ${group.length}`}
          </button>
        )}
      </div>

      {/* Expanded history */}
      {isExpanded && group.slice(0, -1).reverse().map((alert, i) => (
        <div key={i}
          onClick={() => onAlertClick(alert)}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '4px 10px 4px 20px',
            background: isBuy ? '#f1f8f1' : '#fff5f5',
            borderLeft: `3px solid ${color}`,
            cursor: 'pointer', fontSize: '11px', color: '#666',
            marginTop: '1px', borderRadius: '2px',
          }}
        >
          <span style={{ color, fontSize: '11px' }}>{arrow}</span>
          <span style={{ minWidth: '55px', color: '#555' }}>{alert.symbol}</span>
          <span style={{ marginLeft: 'auto' }}>{fmt(alert.pattern_time)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Alert Panel ────────────────────────────────────────────
function AlertPanel({ alerts, activeTab, setActiveTab, onAlertClick }) {
  const [expanded, setExpanded] = useState({});
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current)
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [alerts, activeTab]);

  const tabAlerts = alerts.filter(a => a.timeframe === activeTab);

  // Group by ema → symbol+ema key → sorted list
  const byEma = {};
  tabAlerts.forEach(a => {
    if (!byEma[a.ema]) byEma[a.ema] = {};
    const key = `${a.symbol}_${a.ema}`;
    if (!byEma[a.ema][key]) byEma[a.ema][key] = [];
    byEma[a.ema][key].push(a);
  });
  // Sort each group by pattern_time asc
  Object.values(byEma).forEach(grps =>
    Object.values(grps).forEach(arr =>
      arr.sort((a, b) => new Date(a.pattern_time) - new Date(b.pattern_time))
    )
  );
  const sortedEmas = EMA_OPTIONS.filter(e => byEma[e]);

  const toggleExpand = (key) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // Count unique symbol+ema combos per tab for badge
  const tabCount = (tf) => {
    const ta = alerts.filter(a => a.timeframe === tf);
    const keys = new Set(ta.map(a => `${a.symbol}_${a.ema}`));
    return keys.size;
  };

  return (
    <div style={panel.container}>
      <div style={panel.tabs}>
        {TIMEFRAMES.map(tf => (
          <button key={tf} onClick={() => setActiveTab(tf)}
            style={{ ...panel.tab, ...(activeTab === tf ? panel.tabActive : {}) }}>
            {tf}
            {tabCount(tf) > 0 && (
              <span style={panel.badge}>{tabCount(tf)}</span>
            )}
          </button>
        ))}
      </div>
      <div style={panel.list}>
        {sortedEmas.length === 0 && (
          <div style={panel.empty}>No alerts for {activeTab}</div>
        )}
        {sortedEmas.map(ema => (
          <div key={ema} style={{ marginBottom: '8px' }}>
            <div style={panel.emaHeader}>{EMA_LABEL(ema)}</div>
            {Object.entries(byEma[ema]).map(([key, group]) => (
              <SummaryRow
                key={key}
                group={group}
                isExpanded={!!expanded[key]}
                onToggle={() => toggleExpand(key)}
                onAlertClick={onAlertClick}
              />
            ))}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

const panel = {
  container: {
    width: '280px', minWidth: '280px', display: 'flex',
    flexDirection: 'column', borderRight: '1px solid #e0e0e0',
    background: '#fafafa', height: '100%',
  },
  tabs: {
    display: 'flex', borderBottom: '1px solid #e0e0e0',
    background: '#fff', flexShrink: 0,
  },
  tab: {
    flex: 1, padding: '8px 4px', border: 'none',
    background: 'transparent', cursor: 'pointer',
    fontSize: '11px', color: '#666', position: 'relative',
  },
  tabActive: {
    color: '#2962ff', borderBottom: '2px solid #2962ff', fontWeight: 'bold',
  },
  badge: {
    display: 'inline-block', marginLeft: '3px', background: '#ef5350',
    color: '#fff', borderRadius: '8px', padding: '0 4px', fontSize: '9px',
  },
  list: { flex: 1, overflowY: 'auto', padding: '8px' },
  emaHeader: {
    fontSize: '10px', fontWeight: 'bold', color: '#999',
    textTransform: 'uppercase', padding: '4px 0 2px 0',
    borderBottom: '1px solid #eee', marginBottom: '4px',
  },
  empty: { fontSize: '12px', color: '#aaa', padding: '20px', textAlign: 'center' },
};

// ── Active filter summary for collapsed bar ───────────────
function FilterSummary({ cfg }) {
  const parts = [];
  if (cfg.emas?.length)       parts.push(cfg.emas.map(e => e.replace('ema_','')).join(','));
  if (cfg.timeframes?.length) parts.push(cfg.timeframes.join(','));
  if (cfg.direction && cfg.direction !== 'all') parts.push(cfg.direction.toUpperCase());
  if (cfg.max_gap != null)    parts.push(`Gap≤${cfg.max_gap}`);
  if (cfg.min_inter_gap != null) parts.push(`InterGap≥${cfg.min_inter_gap}`);
  if (cfg.trend_confirmed != null) parts.push(`Trend:${cfg.trend_confirmed ? 'Y' : 'N'}`);
  if (cfg.close_rising != null)    parts.push(`Close:${cfg.close_rising ? '↑' : '↓'}`);
  if (cfg.ema_rising != null)      parts.push(`EMA:${cfg.ema_rising ? '↑' : '↓'}`);
  if (cfg.confirm_break)           parts.push(`CB:${cfg.confirm_break === 'close' ? 'Close' : 'H/L'}`);
  return (
    <span style={{ fontSize: '11px', color: '#555', marginLeft: '8px' }}>
      {parts.length > 0 ? parts.join(' | ') : 'No filters set'}
    </span>
  );
}

// ── Main AlertsView ───────────────────────────────────────
function AlertsView() {
  const { alerts, setAlerts, isRunning } = useStore();
  const [activeTab,    setActiveTab]    = useState('5min');
  const [watchlists,   setWatchlists]   = useState([]);
  const [clearMenu,    setClearMenu]    = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Chart state
  const [chartSymbol,    setChartSymbol]    = useState('');
  const [chartTimeframe, setChartTimeframe] = useState('5min');
  const [chartEma,       setChartEma]       = useState('');
  const { setChartSetting } = useStore();

  // Alert config state
  const [selWatchlist,    setSelWatchlist]    = useState('');
  const [selEmas,         setSelEmas]         = useState([]);
  const [selTimeframes,   setSelTimeframes]   = useState([]);
  const [selDirection,    setSelDirection]    = useState('all');
  const [selMaxGap,       setSelMaxGap]       = useState('');
  const [selMinInterGap,  setSelMinInterGap]  = useState('');
  const [selTrend,        setSelTrend]        = useState('any');
  const [selCloseRising,  setSelCloseRising]  = useState('any');
  const [selEmaRising,    setSelEmaRising]    = useState('any');
  const [selConfirmBreak, setSelConfirmBreak] = useState('any');
  const [saveDefault,     setSaveDefault]     = useState(false);

  // Current saved config — shown in collapsed summary
  const [savedConfig, setSavedConfig] = useState({});

  // Load initial data
  useEffect(() => {
    fetchAllWatchlists()
      .then(data => setWatchlists(data || []))
      .catch(console.error);

    fetchAlertSettings().then(cfg => {
      if (cfg && Object.keys(cfg).length > 0) {
        if (cfg.watchlist)        setSelWatchlist(cfg.watchlist);
        if (cfg.emas)             setSelEmas(cfg.emas);
        if (cfg.timeframes)       setSelTimeframes(cfg.timeframes);
        if (cfg.direction)        setSelDirection(cfg.direction);
        if (cfg.max_gap != null)  setSelMaxGap(String(cfg.max_gap));
        if (cfg.min_inter_gap != null) setSelMinInterGap(String(cfg.min_inter_gap));
        if (cfg.trend_confirmed != null)
          setSelTrend(cfg.trend_confirmed === true ? 'yes' : cfg.trend_confirmed === false ? 'no' : 'any');
        if (cfg.close_rising != null)
          setSelCloseRising(cfg.close_rising === true ? 'yes' : cfg.close_rising === false ? 'no' : 'any');
        if (cfg.ema_rising != null)
          setSelEmaRising(cfg.ema_rising === true ? 'yes' : cfg.ema_rising === false ? 'no' : 'any');
        if (cfg.confirm_break != null)
          setSelConfirmBreak(cfg.confirm_break);
        setSavedConfig(cfg);
      }
    }).catch(console.error);

    fetchAlertsToday()
      .then(data => setAlerts(data || []))
      .catch(console.error);

    if (localStorage.getItem('sse_running') === 'true')
      startSSE(API_BASE);
  }, []);

  // Cleanup store on unmount
  useEffect(() => {
    return () => {
      useStore.getState().setSelectedSymbol('');
      useStore.getState().setChartSetting(0, 'visibleEMAs', {
        ema_5: false, ema_9: false, ema_20: false,
        ema_50: false, ema_200: false,
      });
      useStore.getState().setMarkersConfig({
        enabled:         false,
        fetchFn:         null,
        filters:         {},
        markersBySymbol: null,
      });
    };
  }, []);

  const boolVal = (v) => v === 'yes' ? true : v === 'no' ? false : null;

  const buildConfig = () => ({
    watchlist:         selWatchlist,
    emas:              selEmas,
    timeframes:        selTimeframes,
    direction:         selDirection,
    max_gap:           selMaxGap !== '' ? Number(selMaxGap) : null,
    min_inter_gap:     selMinInterGap !== '' ? Number(selMinInterGap) : null,
    trend_confirmed:   boolVal(selTrend),
    close_rising:      boolVal(selCloseRising),
    ema_rising:        boolVal(selEmaRising),
    confirm_break:     selConfirmBreak === 'any' ? null : selConfirmBreak,
    watchlist_symbols: [],
  });

  const handleSaveAndStart = async () => {
    const config = buildConfig();
    if (selWatchlist) {
      try {
        const wl = watchlists.find(w => String(w.id) === String(selWatchlist));
        if (wl) {
          const data = await fetchWatchlistStocks(wl.id);
          config.watchlist_symbols = (data || []).map(s => s.symbol);
        }
      } catch (_) {}
    }
    try {
      await saveAlertSettings(config);
      setSavedConfig(config);
      setSettingsOpen(false);
    } catch (_) {}
    startSSE(API_BASE);
  };

  const handleAlertClick = (alert) => {
    setChartSymbol(alert.symbol);
    setChartTimeframe(alert.timeframe);
    setChartEma(alert.ema);
    useStore.getState().setSelectedSymbol(alert.symbol);
    setChartSetting(0, 'timeframe', alert.timeframe);
    setChartSetting(0, 'visibleEMAs', {
      ema_5: false, ema_9: false, ema_20: false,
      ema_50: false, ema_200: false,
      [alert.ema]: true,
    });
  };

  const handleClear = async (period) => {
    try {
      await deleteAlertHistory(period);
      if (period === 'all') {
        setAlerts([]);
      } else {
        const cutoff = period === '24h'
          ? Date.now() - 24 * 60 * 60 * 1000
          : Date.now() - 48 * 60 * 60 * 1000;
        setAlerts(prev => prev.filter(
          a => new Date(a.created_at).getTime() < cutoff
        ));
      }
    } catch (_) {}
    setClearMenu(false);
  };

  // Count unique symbol+ema combos for header
  const uniqueCount = new Set(alerts.map(a => `${a.symbol}_${a.ema}_${a.timeframe}`)).size;

  return (
    <div style={s.container}>

      {/* ── Collapsed header bar ── */}
      <div style={s.headerBar}>
        <span style={s.title}>🔴 Live Alerts</span>

        {/* Settings toggle */}
        <button style={s.btnSettings} onClick={() => setSettingsOpen(v => !v)}>
          ⚙ {settingsOpen ? 'Hide Filters ▲' : 'Filters ▼'}
        </button>

        {/* Active filter summary — shown when collapsed */}
        {!settingsOpen && <FilterSummary cfg={savedConfig} />}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Start / Stop */}
          {!isRunning ? (
            <button style={s.btnStart} onClick={handleSaveAndStart}>▶ Start</button>
          ) : (
            <button style={s.btnStop} onClick={stopSSE}>■ Stop</button>
          )}
          {isRunning && <span style={s.liveIndicator}>● LIVE</span>}

          {/* Clear */}
          <div style={{ position: 'relative' }}>
            <button style={s.btnClear} onClick={() => setClearMenu(v => !v)}>
              🗑 Clear ▾
            </button>
            {clearMenu && (
              <div style={s.clearMenu}>
                <button style={s.clearItem} onClick={() => handleClear('24h')}>Last 24 Hours</button>
                <button style={s.clearItem} onClick={() => handleClear('48h')}>Last 48 Hours</button>
                <button style={s.clearItem} onClick={() => handleClear('all')}>All Time</button>
              </div>
            )}
          </div>

          <span style={s.count}>{uniqueCount} alerts today</span>
        </div>
      </div>

      {/* ── Collapsible settings panel ── */}
      {settingsOpen && (
        <div style={s.settingsPanel}>
          <div style={s.settingsGrid}>

            {/* Watchlist */}
            <div style={s.filterGroup}>
              <span style={s.label}>Watchlist</span>
              <select style={s.sel} value={selWatchlist}
                onChange={e => setSelWatchlist(e.target.value)}>
                <option value="">All Watchlists</option>
                {watchlists.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>

            {/* EMA */}
            <div style={s.filterGroup}>
              <span style={s.label}>EMA</span>
              <MultiSelect options={EMA_OPTIONS} selected={selEmas}
                onChange={setSelEmas} labelFn={e => e.replace('ema_', '')} />
            </div>

            {/* Timeframe */}
            <div style={s.filterGroup}>
              <span style={s.label}>Timeframe</span>
              <MultiSelect options={TIMEFRAMES} selected={selTimeframes}
                onChange={setSelTimeframes} />
            </div>

            {/* Direction */}
            <div style={s.filterGroup}>
              <span style={s.label}>Direction</span>
              <SingleSelect
                options={[
                  { value: 'all',  label: 'All' },
                  { value: 'buy',  label: 'Buy' },
                  { value: 'sell', label: 'Sell' },
                ]}
                value={selDirection}
                onChange={setSelDirection}
              />
            </div>

            {/* Max Gap */}
            <div style={s.filterGroup}>
              <span style={s.label}>Max Gap (candles)</span>
              <input style={s.numInput} type="number" min="0" max="50"
                placeholder="Any"
                value={selMaxGap}
                onChange={e => setSelMaxGap(e.target.value)} />
            </div>

            {/* Min Inter-pattern Gap */}
            <div style={s.filterGroup}>
              <span style={s.label}>Min Inter-pattern Gap</span>
              <input style={s.numInput} type="number" min="0" max="500"
                placeholder="Any"
                value={selMinInterGap}
                onChange={e => setSelMinInterGap(e.target.value)} />
            </div>

            {/* Trend Confirmed */}
            <div style={s.filterGroup}>
              <span style={s.label}>Trend Confirmed</span>
              <SingleSelect
                options={[
                  { value: 'any', label: 'Any' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no',  label: 'No'  },
                ]}
                value={selTrend}
                onChange={setSelTrend}
              />
            </div>

            {/* Close Rising */}
            <div style={s.filterGroup}>
              <span style={s.label}>Close Rising</span>
              <SingleSelect
                options={[
                  { value: 'any', label: 'Any' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no',  label: 'No'  },
                ]}
                value={selCloseRising}
                onChange={setSelCloseRising}
              />
            </div>

            {/* EMA Rising */}
            <div style={s.filterGroup}>
              <span style={s.label}>EMA Rising</span>
              <SingleSelect
                options={[
                  { value: 'any', label: 'Any' },
                  { value: 'yes', label: 'Yes' },
                  { value: 'no',  label: 'No'  },
                ]}
                value={selEmaRising}
                onChange={setSelEmaRising}
              />
            </div>

            {/* Confirm Break */}
            <div style={s.filterGroup}>
              <span style={s.label}>Confirm Break</span>
              <SingleSelect
                options={[
                  { value: 'any',   label: 'Any' },
                  { value: 'close', label: 'Close basis' },
                  { value: 'hl',    label: 'High/Low basis' },
                ]}
                value={selConfirmBreak}
                onChange={setSelConfirmBreak}
              />
            </div>

          </div>

          {/* Save / apply row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
            
            <button style={s.btnApply} onClick={handleSaveAndStart}>
              ▶ Apply & Start
            </button>
          </div>
        </div>
      )}

      {/* ── Main body ── */}
      <div style={s.body}>
        <AlertPanel
          alerts={alerts}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          onAlertClick={handleAlertClick}
        />
        <div style={s.chartArea}>
          {chartSymbol ? (
            <Chart key={`${chartSymbol}-${chartTimeframe}-${chartEma}`} gridIndex={0} />
          ) : (
            <div style={s.noChart}>Click an alert to view chart</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────
const s = {
  container: {
    display: 'flex', flexDirection: 'column',
    height: '100%', overflow: 'hidden', background: '#f5f5f5',
  },
  headerBar: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '6px 16px', background: '#fff',
    borderBottom: '1px solid #e0e0e0', flexShrink: 0, flexWrap: 'wrap',
  },
  title: { fontWeight: 'bold', fontSize: '14px', color: '#131722' },
  btnSettings: {
    padding: '4px 10px', background: '#f0f0f0', border: '1px solid #ddd',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#444',
  },
  settingsPanel: {
    padding: '12px 16px', background: '#fafafa',
    borderBottom: '2px solid #e0e0e0', flexShrink: 0,
  },
  settingsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '10px',
  },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
  label: { fontSize: '11px', color: '#666', fontWeight: 'bold' },
  sel: {
    padding: '4px 8px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '12px', color: '#444',
  },
  numInput: {
    width: '80px', padding: '4px 8px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '12px', color: '#444',
  },
  btnApply: {
    padding: '5px 16px', background: '#2e7d32', color: '#fff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '12px', fontWeight: 'bold',
  },
  btnStart: {
    padding: '5px 14px', background: '#2e7d32', color: '#fff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '12px', fontWeight: 'bold',
  },
  btnStop: {
    padding: '5px 14px', background: '#d32f2f', color: '#fff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
    fontSize: '12px', fontWeight: 'bold',
  },
  liveIndicator: { color: '#d32f2f', fontWeight: 'bold', fontSize: '12px' },
  btnClear: {
    padding: '4px 10px', background: '#f5f5f5', border: '1px solid #ddd',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', color: '#555',
  },
  clearMenu: {
    position: 'absolute', top: '100%', right: 0, background: '#fff',
    border: '1px solid #ddd', borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 100, minWidth: '140px',
  },
  clearItem: {
    display: 'block', width: '100%', padding: '8px 12px',
    background: 'none', border: 'none', textAlign: 'left',
    cursor: 'pointer', fontSize: '12px', color: '#444',
  },
  count: { fontSize: '12px', color: '#888' },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  chartArea: { flex: 1, overflow: 'hidden', position: 'relative' },
  noChart: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100%', color: '#aaa', fontSize: '14px',
  },
};

export default AlertsView;